<template>
  <div class="report-page">
    <div class="page-header">
      <h2 class="page-title">碳排放报告管理</h2>
    </div>

    <filter-bar>
      <div class="filter-item">
        <span class="filter-label">报告年度</span>
        <el-date-picker
          v-model="year"
          type="year"
          size="small"
          placeholder="全部"
          clearable
          value-format="yyyy"
          style="width: 120px"
        />
      </div>
      <div class="filter-item">
        <span class="filter-label">核算标准</span>
        <el-select v-model="standard" size="small" clearable placeholder="全部" style="width: 160px">
          <el-option v-for="item in standards" :key="item" :label="item" :value="item" />
        </el-select>
      </div>
      <div class="filter-item">
        <span class="filter-label">报告状态</span>
        <el-select v-model="status" size="small" clearable placeholder="全部" style="width: 120px">
          <el-option
            v-for="item in statusOptions"
            :key="item.value"
            :label="item.label"
            :value="item.value"
          />
        </el-select>
      </div>
      <el-button type="primary" size="small" @click="loadData">查询</el-button>
    </filter-bar>

    <div class="stat-cards-row">
      <stat-card
        title="报告总数"
        :today="summary.total.today"
        :annual="summary.total.annual"
        :unit="summary.total.unit"
        :annual-unit="summary.total.unit"
        icon="el-icon-document"
        color="#409EFF"
      />
      <stat-card
        title="编制中"
        :today="summary.drafting.today"
        :annual="summary.drafting.annual"
        :unit="summary.drafting.unit"
        :annual-unit="summary.drafting.unit"
        icon="el-icon-edit-outline"
        color="#E6A23C"
      />
      <stat-card
        title="已发布"
        :today="summary.published.today"
        :annual="summary.published.annual"
        :unit="summary.published.unit"
        :annual-unit="summary.published.unit"
        icon="el-icon-circle-check"
        color="#67C23A"
      />
      <stat-card
        title="年度排放总量"
        :today="summary.totalEmission.today"
        :annual="summary.totalEmission.annual"
        :unit="summary.totalEmission.unit"
        :annual-unit="summary.totalEmission.unit"
        icon="el-icon-data-analysis"
        color="#1a3a5c"
      />
    </div>

    <div class="chart-section">
      <chart-card title="各 Scope 排放占比" class="scope-chart">
        <base-chart :option="scopeChartOption" />
      </chart-card>
      <chart-card title="报告状态分布" class="status-chart">
        <base-chart :option="statusChartOption" />
      </chart-card>
    </div>

    <div class="table-section">
      <div class="table-toolbar">
        <el-button type="primary" size="small" icon="el-icon-plus">新建报告</el-button>
      </div>
      <el-table :data="reports" border stripe size="small">
        <el-table-column prop="name" label="报告名称" min-width="220" show-overflow-tooltip />
        <el-table-column prop="year" label="核算年度" width="100" align="center" />
        <el-table-column prop="standard" label="核算标准" width="140" />
        <el-table-column prop="boundaryVersion" label="边界版本" width="110" align="center" />
        <el-table-column prop="totalEmission" label="总排放量 (tCO2e)" width="160" align="right">
          <template slot-scope="{ row }">
            {{ row.totalEmission ? formatNumber(row.totalEmission, 4) : '-' }}
          </template>
        </el-table-column>
        <el-table-column prop="statusLabel" label="状态" width="100" align="center">
          <template slot-scope="{ row }">
            <el-tag :type="statusTagType(row.status)" size="mini">{{ row.statusLabel }}</el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="createTime" label="创建时间" width="160" />
        <el-table-column label="操作" width="200" align="center" fixed="right">
          <template slot-scope="{ row }">
            <el-button type="text" size="small" @click="openDetail(row)">查看详情</el-button>
            <el-button type="text" size="small" @click="handleExport(row)">导出</el-button>
            <el-button
              v-if="row.status === 'drafting'"
              type="text"
              size="small"
              @click="handleSubmit(row)"
            >提交审核</el-button>
          </template>
        </el-table-column>
      </el-table>
    </div>

    <el-drawer
      :title="currentReport ? currentReport.name : '报告详情'"
      :visible.sync="drawerVisible"
      size="520px"
      direction="rtl"
    >
      <div v-if="currentReport" class="drawer-content">
        <el-descriptions :column="1" border size="small" class="detail-block">
          <el-descriptions-item label="核算年度">{{ currentReport.year }}</el-descriptions-item>
          <el-descriptions-item label="核算标准">{{ currentReport.standard }}</el-descriptions-item>
          <el-descriptions-item label="边界版本">{{ currentReport.boundaryVersion }}</el-descriptions-item>
          <el-descriptions-item label="总排放量">
            {{ currentReport.totalEmission ? formatNumber(currentReport.totalEmission, 4) + ' tCO2e' : '-' }}
          </el-descriptions-item>
          <el-descriptions-item label="状态">
            <el-tag :type="statusTagType(currentReport.status)" size="mini">
              {{ currentReport.statusLabel }}
            </el-tag>
          </el-descriptions-item>
        </el-descriptions>

        <div v-if="currentReport.scopeBreakdown.length" class="detail-block">
          <div class="block-title">Scope 分项排放</div>
          <el-table :data="currentReport.scopeBreakdown" border stripe size="small">
            <el-table-column prop="scope" label="Scope" width="100" />
            <el-table-column prop="emission" label="排放量 (tCO2e)" align="right">
              <template slot-scope="{ row }">{{ formatNumber(row.emission, 4) }}</template>
            </el-table-column>
            <el-table-column prop="ratio" label="占比 (%)" width="100" align="right">
              <template slot-scope="{ row }">{{ formatNumber(row.ratio, 2) }}</template>
            </el-table-column>
          </el-table>
        </div>

        <div v-if="currentReport.topSources.length" class="detail-block">
          <div class="block-title">主要排放源 Top5</div>
          <el-table :data="currentReport.topSources" border stripe size="small">
            <el-table-column type="index" label="排名" width="60" align="center" />
            <el-table-column prop="name" label="排放源" min-width="140" />
            <el-table-column prop="emission" label="排放量 (tCO2e)" align="right">
              <template slot-scope="{ row }">{{ formatNumber(row.emission, 4) }}</template>
            </el-table-column>
            <el-table-column prop="ratio" label="占比 (%)" width="90" align="right">
              <template slot-scope="{ row }">{{ formatNumber(row.ratio, 2) }}</template>
            </el-table-column>
          </el-table>
        </div>
      </div>
    </el-drawer>
  </div>
</template>

<script>
import FilterBar from '@/components/common/FilterBar.vue'
import StatCard from '@/components/common/StatCard.vue'
import ChartCard from '@/components/common/ChartCard.vue'
import BaseChart from '@/components/charts/BaseChart.vue'
import { getReportData } from '@/api/modules/carbonAccounting'
import { formatNumber } from '@/utils/format'

export default {
  name: 'CarbonAccountingReport',
  components: { FilterBar, StatCard, ChartCard, BaseChart },
  data() {
    return {
      year: '',
      standard: '',
      status: '',
      standards: [],
      statusOptions: [],
      summary: {
        total: { today: 0, annual: 0, unit: '份' },
        drafting: { today: 0, annual: 0, unit: '份' },
        published: { today: 0, annual: 0, unit: '份' },
        totalEmission: { today: 0, annual: 0, unit: 'tCO2e' }
      },
      reports: [],
      scopeChartOption: {},
      statusChartOption: {},
      drawerVisible: false,
      currentReport: null
    }
  },
  created() {
    this.loadData()
  },
  methods: {
    formatNumber,
    async loadData() {
      const res = await getReportData({
        year: this.year,
        standard: this.standard,
        status: this.status
      })
      const data = res.data
      this.standards = data.standards
      this.statusOptions = data.statusOptions
      this.summary = data.summary
      this.reports = data.reports
      this.buildScopeChart(data.scopeShare)
      this.buildStatusChart(data.statusDist)
    },
    buildScopeChart(share) {
      this.scopeChartOption = {
        tooltip: { trigger: 'item', formatter: '{b}: {c} tCO2e ({d}%)' },
        legend: { orient: 'vertical', right: 10, top: 'center' },
        color: ['#1a3a5c', '#409EFF', '#E6A23C'],
        series: [{
          type: 'pie',
          radius: ['40%', '65%'],
          center: ['40%', '50%'],
          data: share,
          label: { formatter: '{b}\n{d}%' }
        }]
      }
    },
    buildStatusChart(dist) {
      this.statusChartOption = {
        tooltip: { trigger: 'axis' },
        grid: { left: 50, right: 20, top: 30, bottom: 40 },
        xAxis: { type: 'category', data: dist.map(d => d.name) },
        yAxis: { type: 'value', name: '份', minInterval: 1 },
        series: [{
          type: 'bar',
          data: dist.map(d => d.value),
          itemStyle: {
            color: params => ['#67C23A', '#409EFF', '#E6A23C', '#909399'][params.dataIndex]
          },
          barWidth: 40
        }]
      }
    },
    statusTagType(status) {
      const map = {
        published: 'success',
        reviewing: 'warning',
        drafting: '',
        draft: 'info'
      }
      return map[status] || 'info'
    },
    openDetail(row) {
      this.currentReport = row
      this.drawerVisible = true
    },
    handleExport(row) {
      this.$message.success(`正在导出「${row.name}」`)
    },
    handleSubmit(row) {
      row.status = 'reviewing'
      row.statusLabel = '待审核'
      this.$message.success('已提交审核')
    }
  }
}
</script>

<style lang="less" scoped>
@import '~@/styles/variables.less';

.page-header {
  margin-bottom: 16px;

  .page-title {
    font-size: 18px;
    font-weight: 600;
    color: @text-primary;
    margin: 0;
  }
}

.stat-cards-row {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 16px;
  margin-bottom: 16px;
}

.chart-section {
  display: flex;
  gap: 16px;
  height: 320px;
  margin-bottom: 16px;

  .scope-chart,
  .status-chart {
    flex: 1;
  }
}

.table-section {
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  padding: 16px;
}

.table-toolbar {
  margin-bottom: 12px;
}

.drawer-content {
  padding: 0 4px;
}

.detail-block {
  margin-bottom: 20px;

  .block-title {
    font-size: 14px;
    font-weight: 600;
    margin-bottom: 8px;
    padding-left: 8px;
    border-left: 3px solid @active-color;
  }
}
</style>
